import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Send } from "lucide-react";
import {
  cancelChatAgentAction,
  confirmChatAgentAction,
  postChatAgentMessage,
  type ChatAgentMessageResponse,
  type ChatAgentOption,
  type ChatAgentPreview,
  type ChatAgentReport,
  type ChatAgentReportColumn,
} from "./agentChatAgentApi";
import "./AgentChatAgent.css";

type UiRole = "user" | "bot" | "system";

type UiMessage = {
  id: string;
  role: UiRole;
  text: string;
  options?: ChatAgentOption[];
  preview?: ChatAgentPreview;
  report?: ChatAgentReport;
  showConfirmActions?: boolean;
  orderNumber?: string;
};

/** Project chat persona for agent order flow */
const AGENT_DISPLAY_NAME = "Really Esy";

const DEFAULT_WELCOME = `Hi, I'm ${AGENT_DISPLAY_NAME}, your order assistant.\nHow can I help you today?`;

function uid() {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function money(n?: number, currency = "USD") {
  if (n == null || Number.isNaN(Number(n))) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
    }).format(Number(n));
  } catch {
    return `$${Number(n).toFixed(2)}`;
  }
}

function reportLabel(value: string) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function reportValue(value: unknown, column: ChatAgentReportColumn) {
  if (value == null || value === "") return "—";
  if (column.format === "currency" && !Number.isNaN(Number(value))) {
    return money(Number(value));
  }
  if (column.data_type === "datetime") {
    const date = new Date(String(value));
    if (!Number.isNaN(date.getTime())) {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
    }
  }
  if ((column.data_type === "number" || column.data_type === "integer") && !Number.isNaN(Number(value))) {
    return Number(value).toLocaleString();
  }
  return String(value);
}

const ReportCard: React.FC<{ report: ChatAgentReport }> = ({ report }) => {
  const columns = Array.isArray(report.columns) ? report.columns : [];
  const rows = Array.isArray(report.rows) ? report.rows : [];
  const summary = Object.entries(report.summary || {}).filter(([, value]) => value != null);

  return (
    <section className="aca-report" aria-label={report.title || "Report results"}>
      <div className="aca-report-heading">
        <div>
          <h4>{report.title || "Report results"}</h4>
          {report.period ? <p>{report.period}</p> : null}
        </div>
        <span className="aca-report-count">{rows.length} rows</span>
      </div>

      {summary.length > 0 ? (
        <div className="aca-report-summary">
          {summary.map(([key, value]) => (
            <div className="aca-report-stat" key={key}>
              <span>{reportLabel(key)}</span>
              <strong>{key.includes("spend") || key.includes("revenue") || key.includes("tax") ? money(value) : value.toLocaleString()}</strong>
            </div>
          ))}
        </div>
      ) : null}

      {columns.length > 0 ? (
        <div className="aca-report-table-wrap">
          <table className="aca-report-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} scope="col">{column.label || reportLabel(column.key)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? rows.map((row, rowIndex) => (
                <tr key={`${String(row.order_number || "row")}_${rowIndex}`}>
                  {columns.map((column) => (
                    <td key={column.key}>{reportValue(row[column.key], column)}</td>
                  ))}
                </tr>
              )) : (
                <tr>
                  <td className="aca-report-empty" colSpan={columns.length}>No matching records.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="aca-report-footer">
        <span>Page {report.page || 1}</span>
        {report.has_more ? <span>More results available</span> : null}
      </div>
    </section>
  );
};

function isTerminalState(state?: string) {
  const s = String(state || "").toUpperCase();
  return s === "COMPLETED" || s === "CANCELED" || s === "FAILED";
}

function isReadyForConfirm(state?: string) {
  return String(state || "").toUpperCase() === "READY_FOR_CONFIRMATION";
}

/** Chat-bot mark: speech bubble with a robot face cut out, tinted by currentColor */
const AssistantMark: React.FC<{ size?: number; className?: string }> = ({
  size = 28,
  className,
}) => {
  const maskId = `aca-mark-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <mask id={maskId}>
          <rect width="32" height="32" fill="#000" />
          <rect x="3" y="6" width="26" height="19" rx="7" fill="#fff" />
          <path d="M9.5 23.5h8l-8 6.5z" fill="#fff" />
          <rect x="15.1" y="2.6" width="1.8" height="4" rx="0.9" fill="#fff" />
          <circle cx="16" cy="2.2" r="1.7" fill="#fff" />
          <circle cx="11.8" cy="14" r="2.3" fill="#000" />
          <circle cx="20.2" cy="14" r="2.3" fill="#000" />
          <path
            d="M11.8 18.6c1.3 1.7 2.7 2.5 4.2 2.5s2.9-.8 4.2-2.5"
            stroke="#000"
            strokeWidth="1.9"
            strokeLinecap="round"
            fill="none"
          />
        </mask>
      </defs>
      <rect width="32" height="32" fill="currentColor" mask={`url(#${maskId})`} />
    </svg>
  );
};

/**
 * Floating chat agent for Agent login.
 * Mount once in AgentLayout. Comment out that mount to disable for demos.
 */
const AgentChatAgent: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [booting, setBooting] = useState(false);
  const [conversationId, setConversationId] = useState("");
  const [actionId, setActionId] = useState("");
  const [stateLabel, setStateLabel] = useState("");
  const [messages, setMessages] = useState<UiMessage[]>([
    {
      id: "welcome",
      role: "bot",
      text: DEFAULT_WELCOME,
    },
  ]);

  const conversationIdRef = useRef("");
  const startRequestIdRef = useRef(0);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    if (!open) return;
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open, sending, booting]);

  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => inputRef.current?.focus(), 220);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  const appendBotFromResponse = useCallback((res: ChatAgentMessageResponse) => {
    if (res.conversation_id) {
      const id = String(res.conversation_id);
      conversationIdRef.current = id;
      setConversationId(id);
    }
    if (res.action_id) setActionId(String(res.action_id));
    if (res.state) setStateLabel(String(res.state));

    if (res.error) {
      setMessages((prev) => [
        ...prev,
        { id: uid(), role: "bot", text: res.error || "Something went wrong." },
      ]);
      return;
    }

    const text =
      res.message ||
      (res.order?.order_number
        ? `Draft sales order ${res.order.order_number} created.`
        : "Got it.");

    setMessages((prev) => [
      ...prev,
      {
        id: uid(),
        role: "bot",
        text,
        options: Array.isArray(res.options) ? res.options : undefined,
        preview: res.preview,
        report: res.report,
        showConfirmActions: isReadyForConfirm(res.state),
        orderNumber: res.order?.order_number,
      },
    ]);

    if (isTerminalState(res.state) && String(res.state).toUpperCase() !== "COMPLETED") {
      conversationIdRef.current = "";
      setConversationId("");
      setActionId("");
    }
  }, []);

  /** Open chat: POST message "start" with empty conversation_id */
  const startSession = useCallback(async () => {
    const requestId = ++startRequestIdRef.current;
    setBooting(true);
    setSending(false);
    setInput("");
    conversationIdRef.current = "";
    setConversationId("");
    setActionId("");
    setStateLabel("");
    setMessages([{ id: "welcome", role: "bot", text: DEFAULT_WELCOME }]);

    try {
      const res = await postChatAgentMessage({
        conversation_id: "",
        message: "start",
        input_type: "text",
      });
      if (requestId !== startRequestIdRef.current) return;
      appendBotFromResponse(res);
    } catch (err) {
      if (requestId !== startRequestIdRef.current) return;
      setMessages((prev) => [
        ...prev,
        {
          id: uid(),
          role: "bot",
          text:
            err instanceof Error
              ? `I couldn't start the chat (${err.message}). Please try again.`
              : "I couldn't start the chat. Please try again.",
        },
      ]);
    } finally {
      if (requestId === startRequestIdRef.current) setBooting(false);
    }
  }, [appendBotFromResponse]);

  const openChat = () => {
    setOpen(true);
    void startSession();
  };

  /** Close chat: POST message "close" with latest conversation_id */
  const closeChat = async () => {
    const latestConversationId = conversationIdRef.current || "";
    startRequestIdRef.current += 1;
    setOpen(false);
    setBooting(false);
    setSending(false);
    setInput("");
    setActionId("");
    setStateLabel("");
    setMessages([{ id: "welcome", role: "bot", text: DEFAULT_WELCOME }]);

    try {
      await postChatAgentMessage({
        conversation_id: latestConversationId,
        message: "close",
        input_type: "text",
      });
    } catch {
      // Close UI anyway; session cleanup is best-effort
    } finally {
      conversationIdRef.current = "";
      setConversationId("");
    }
  };

  const sendMessage = useCallback(
    async (raw: string, displayText?: string) => {
      const message = String(raw || "").trim();
      if (!message || sending || booting) return;

      setSending(true);
      setInput("");
      setMessages((prev) => [
        ...prev,
        { id: uid(), role: "user", text: displayText?.trim() || message },
      ]);

      try {
        const res = await postChatAgentMessage({
          conversation_id: conversationIdRef.current || conversationId,
          message,
          input_type: "text",
        });
        appendBotFromResponse(res);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            id: uid(),
            role: "bot",
            text: err instanceof Error ? err.message : "Failed to reach chat agent.",
          },
        ]);
      } finally {
        setSending(false);
      }
    },
    [appendBotFromResponse, booting, conversationId, sending]
  );

  const onPickOption = (opt: ChatAgentOption) => {
    void sendMessage(String(opt.option), opt.label);
  };

  const onConfirm = async () => {
    if (!actionId || sending || booting) return;
    setSending(true);
    setMessages((prev) => [...prev, { id: uid(), role: "user", text: "yes" }]);
    try {
      const res = await confirmChatAgentAction(actionId);
      appendBotFromResponse(res);
    } catch (err) {
      try {
        const res = await postChatAgentMessage({
          conversation_id: conversationIdRef.current || conversationId,
          message: "yes",
          input_type: "text",
        });
        appendBotFromResponse(res);
      } catch (inner) {
        setMessages((prev) => [
          ...prev,
          {
            id: uid(),
            role: "bot",
            text:
              inner instanceof Error
                ? inner.message
                : err instanceof Error
                  ? err.message
                  : "Could not confirm order.",
          },
        ]);
      }
    } finally {
      setSending(false);
    }
  };

  const onCancel = async () => {
    if (sending || booting) return;
    setSending(true);
    setMessages((prev) => [...prev, { id: uid(), role: "user", text: "cancel" }]);
    try {
      if (actionId) {
        const res = await cancelChatAgentAction(actionId);
        appendBotFromResponse(res);
      } else {
        const res = await postChatAgentMessage({
          conversation_id: conversationIdRef.current || conversationId,
          message: "cancel",
          input_type: "text",
        });
        appendBotFromResponse(res);
      }
      conversationIdRef.current = "";
      setConversationId("");
      setActionId("");
      setStateLabel("CANCELED");
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: uid(),
          role: "bot",
          text: err instanceof Error ? err.message : "Could not cancel.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onNewChat = () => {
    void startSession();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  };

  return (
    <div className="aca-root" aria-live="polite">
      {!open ? (
        <button
          type="button"
          className="aca-fab"
          title={`Chat with ${AGENT_DISPLAY_NAME}`}
          aria-label={`Open ${AGENT_DISPLAY_NAME} chat`}
          onClick={openChat}
        >
          <AssistantMark size={30} className="aca-fab-icon" />
        </button>
      ) : (
        <div className="aca-panel" role="dialog" aria-label={`${AGENT_DISPLAY_NAME} chat`}>
          <div className="aca-header">
            <div className="aca-header-left">
              <div className="aca-avatar" aria-hidden>
                <AssistantMark size={22} className="aca-avatar-icon" />
                <span className="aca-online-dot" />
              </div>
              <div className="aca-title-wrap">
                <h3 className="aca-title">{AGENT_DISPLAY_NAME}</h3>
                <p className="aca-subtitle">
                  {booting ? "Starting chat…" : "Your assistant"}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="aca-close"
              aria-label="Close chat"
              title="Close"
              onClick={() => void closeChat()}
            >
              <svg
                className="aca-close-icon"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>

          <div className="aca-body" ref={bodyRef}>
            {stateLabel ? <div className="aca-state-pill">{stateLabel.replace(/_/g, " ")}</div> : null}

            {messages.map((m) => (
              <div
                key={m.id}
                className={`aca-msg ${m.role === "user" ? "aca-msg--user" : "aca-msg--bot"}`}
              >
                <div className="aca-bubble">{m.text}</div>

                {m.options && m.options.length > 0 ? (
                  <div className="aca-options">
                    <span className="aca-options-hint">Choose an option</span>
                    {m.options.map((opt) => (
                      <button
                        key={`${m.id}_${opt.option}_${opt.reference_token || ""}`}
                        type="button"
                        className="aca-option"
                        disabled={sending || booting}
                        onClick={() => onPickOption(opt)}
                      >
                        <span className="aca-option-num">{opt.option}</span>
                        <span className="aca-option-label">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                ) : null}

                {m.report ? <ReportCard report={m.report} /> : null}

                {m.preview ? (
                  <div className="aca-preview">
                    <h4>Order preview</h4>
                    {m.preview.vendor ? (
                      <div className="aca-preview-row">
                        <span>Vendor</span>
                        <span>{m.preview.vendor}</span>
                      </div>
                    ) : null}
                    {m.preview.product ? (
                      <div className="aca-preview-row">
                        <span>Product</span>
                        <span>{m.preview.product}</span>
                      </div>
                    ) : null}
                    {m.preview.sku ? (
                      <div className="aca-preview-row">
                        <span>SKU</span>
                        <span>{m.preview.sku}</span>
                      </div>
                    ) : null}
                    {m.preview.quantity != null ? (
                      <div className="aca-preview-row">
                        <span>Qty</span>
                        <span>{m.preview.quantity}</span>
                      </div>
                    ) : null}
                    {m.preview.shipping_method ? (
                      <div className="aca-preview-row">
                        <span>Shipping</span>
                        <span>{m.preview.shipping_method}</span>
                      </div>
                    ) : null}
                    {m.preview.billing_address ? (
                      <div className="aca-preview-row">
                        <span>Billing</span>
                        <span>{m.preview.billing_address}</span>
                      </div>
                    ) : null}
                    {m.preview.shipping_address ? (
                      <div className="aca-preview-row">
                        <span>Ship to</span>
                        <span>{m.preview.shipping_address}</span>
                      </div>
                    ) : null}
                    <div className="aca-preview-row aca-preview-total">
                      <span>Total</span>
                      <span>{money(m.preview.total, m.preview.currency)}</span>
                    </div>
                  </div>
                ) : null}

                {m.showConfirmActions ? (
                  <div className="aca-actions">
                    <button
                      type="button"
                      className="aca-btn aca-btn--primary"
                      disabled={sending || booting}
                      onClick={() => void onConfirm()}
                    >
                      Confirm draft
                    </button>
                    <button
                      type="button"
                      className="aca-btn aca-btn--ghost"
                      disabled={sending || booting}
                      onClick={() => void onCancel()}
                    >
                      Cancel
                    </button>
                  </div>
                ) : null}

                {m.orderNumber ? (
                  <div className="aca-actions">
                    <button
                      type="button"
                      className="aca-btn aca-btn--ghost"
                      disabled={sending || booting}
                      onClick={onNewChat}
                    >
                      Start new order
                    </button>
                  </div>
                ) : null}
              </div>
            ))}

            {sending || booting ? (
              <div
                className="aca-typing"
                aria-label={booting ? "Starting chat" : "Assistant is typing"}
              >
                <span />
                <span />
                <span />
              </div>
            ) : null}
          </div>

          <div className="aca-footer">
            <div className="aca-input-wrap">
              <textarea
                ref={inputRef}
                className="aca-input"
                rows={1}
                placeholder="Type a message…"
                value={input}
                disabled={sending || booting}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
              />
            </div>
            <button
              type="button"
              className="aca-send"
              aria-label="Send"
              disabled={sending || booting || !input.trim()}
              onClick={() => void sendMessage(input)}
            >
              <Send size={26} strokeWidth={2.35} className="aca-send-icon" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentChatAgent;
